import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, BookOpen } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { Loader } from "@/components/shared/Loader";

export default function EducationArticle() {
  const { slug } = useParams();
  const { t } = useLang();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useSEO(article?.title || "Article");

  useEffect(() => {
    api.get(`/education/articles/${slug}`).then((r) => {
      if (r.data && r.data.id) setArticle(r.data); else setNotFound(true);
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-slate-400">Article not found.</p>
      <Link to="/education" className="mt-4 inline-block text-brand-cyan hover:underline">← {t("nav_education")}</Link>
    </div>
  );
  if (!article) return <Loader full={false} />;

  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16">
      <Link to="/education" className="inline-flex items-center gap-2 text-sm text-brand-cyan hover:underline mb-8" data-testid="article-back"><ArrowLeft className="h-4 w-4 rtl-mirror" /> {t("nav_education")}</Link>
      <div className="flex items-center gap-3 text-[11px] font-mono text-brand-cyan uppercase"><Clock className="h-3.5 w-3.5" /> {article.reading_time || 5} min read {article.author && <span className="text-slate-500">· {article.author}</span>}</div>
      <h1 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold text-white leading-tight">{article.title}</h1>
      {article.excerpt && <p className="mt-4 text-lg text-slate-400">{article.excerpt}</p>}
      {article.cover_image_file_id && <img src={fileUrl(article.cover_image_file_id)} alt={article.title} className="mt-8 w-full rounded-2xl border border-white/10" />}
      <div className="mt-8 prose prose-invert max-w-none text-slate-300 leading-relaxed whitespace-pre-wrap" data-testid="article-content">{article.content}</div>
      {article.tags?.length > 0 && (
        <div className="mt-10 flex flex-wrap gap-2">
          {article.tags.map((tag) => <span key={tag} className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-400">#{tag}</span>)}
        </div>
      )}
    </article>
  );
}
